import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, animate, motion, useMotionValue, useReducedMotion, useScroll, type MotionValue } from "framer-motion";
import type { PageProps } from "@/lib/header-config";
import { TimelineSection } from "@/pages/homepage/components/TimelineSection";
import { FadeUp, SlideIn } from "@/pages/homepage/components/motion";
import { TIMELINE } from "@/pages/homepage/content/homepage";
import { EVENTS } from "@/pages/homepage/content/events";
import { MEMBERS } from "@/pages/homepage/content/members";
import { useActiveSectionTracker } from "@/pages/homepage/hooks/useActiveSectionTracker";
import {
  scrollTo,
  useCursorFollower,
  useParallaxEffect,
  useScrollProgress,
  useSectionBackground,
} from "@/pages/homepage/hooks/useHomepageEffects";
import { CTASection } from "@/pages/homepage/sections/CTASection";
import { StatsSection } from "@/pages/homepage/sections/StatsSection";
import { Footer } from "@/components/Footer";

/* ─── Local assets ────────────────────────────────────────────────────── */
import imgDesktop26 from "@/assets/layer-3-img-1.webp";
import imgRectangle from "@/assets/hero-bg.jpg";

const glowColors = [
  "#3B82F6","#6366F1","#8B5CF6","#A78BFA",
  "#60A5FA","#818CF8","#7C3AED","#4F46E5",
  "#2563EB","#7C3AED","#6D28D9","#4338CA",
  "#3B82F6","#60A5FA","#818CF8","#8B5CF6",
];

const SECTION_HEADING_SIZE = "clamp(30px, 4.2vw, 52px)";

const KEYFRAMES = `
  @keyframes marquee-left  { from{transform:translateX(0)}    to{transform:translateX(-50%)} }
  @keyframes marquee-right { from{transform:translateX(-50%)} to{transform:translateX(0)}    }
  @keyframes float         { 0%,100%{transform:translateY(0)}   50%{transform:translateY(-14px)} }
  @keyframes pulse-glow    { 0%,100%{opacity:.7;transform:scale(1)}  50%{opacity:1;transform:scale(1.08)} }
  @keyframes scroll-bounce { 0%,100%{transform:translateY(0)}  50%{transform:translateY(8px)} }
`;



/* ─── MacBook display frame ─────────────────────────────────────────── */
function MacFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative w-full max-h-full overflow-hidden rounded-2xl" style={{ aspectRatio: '195.75 / 115' }}>
      {children}
    </div>
  );
}

function EventSpotlightCard({
  title,
  titleHighlight,
  description,
  imgSrc,
}: {
  index: number;
  title: string;
  titleHighlight: string;
  description: React.ReactNode;
  imgSrc: string;
}) {
  return (
    <div className="flex h-full w-full flex-col items-start gap-5 px-4 md:px-8">
      <div className="flex w-full min-w-0 shrink-0 flex-col items-start gap-3">
        <div className="flex flex-col items-start font-bold tracking-[-1.5px]" style={{ fontSize: "clamp(32px,4vw,50px)" }}>
          <span className="leading-[1.3] text-ink">{title}</span>
          <span className="leading-[1.3] text-brand">{titleHighlight}</span>
        </div>
        <div className="font-medium tracking-tight-xl leading-[1.22] text-black/58" style={{ fontSize: "clamp(16px,1.55vw,22px)" }}>
          {description}
        </div>
      </div>

      <div className="w-full min-h-0 flex-1 max-w-245">
        <MacFrame>
          <img
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
            src={imgSrc}
          />
        </MacFrame>
      </div>
    </div>
  );
}

function EventScrollShowcase() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const reducedMotion = !!useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  useEffect(() => {
    return scrollYProgress.on("change", (value) => {
      const nextIndex = Math.min(
        EVENTS.length - 1,
        Math.max(0, Math.round(value * (EVENTS.length - 1))),
      );
      setActiveIndex((prev) => (prev === nextIndex ? prev : nextIndex));
    });
  }, [scrollYProgress]);

  return (
    <div
      ref={sectionRef}
      className="relative w-full"
      style={{ height: `calc(${EVENTS.length} * 112vh)` }}
    >
      <div className="sticky top-0 h-screen">

        {/* ── Mobile layout ── */}
        <div className="flex h-full flex-col px-6 pt-28 pb-8 lg:hidden">
          <div className="shrink-0 pb-5">
            <div className="text-[clamp(26px,7vw,36px)] font-bold leading-[1.22] tracking-tight-xl text-ink">
              한 해동안 함께할
              <br />
              주요 활동들이에요.
            </div>
          </div>
          <div className="flex shrink-0 items-center justify-start gap-1.5 pb-5">
            {EVENTS.map((_, i) => (
              <motion.div
                key={i}
                className="rounded-full bg-brand"
                animate={{ width: i === activeIndex ? 20 : 6, opacity: i === activeIndex ? 1 : 0.3 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                style={{ height: 6 }}
              />
            ))}
          </div>
          <div className="relative min-h-0 flex-1">
            <AnimatePresence mode="popLayout" initial={false}>
              {EVENTS.map((event, index) =>
                index === activeIndex ? (
                  <motion.div
                    key={event.titleHighlight}
                    className="absolute inset-0 flex flex-col gap-5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    <div className="w-full shrink-0">
                      <MacFrame>
                        <img alt="" className="absolute inset-0 h-full w-full object-cover object-center" src={event.imgSrc} />
                      </MacFrame>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col font-bold tracking-[-1.5px]" style={{ fontSize: "clamp(26px,7vw,36px)" }}>
                        <span className="leading-[1.3] text-ink">{event.title}</span>
                        <span className="leading-[1.3] text-brand">{event.titleHighlight}</span>
                      </div>
                      <div className="font-medium leading-[1.3] tracking-[-0.03em] text-black/58" style={{ fontSize: "clamp(14px,4vw,17px)" }}>
                        {event.description}
                      </div>
                    </div>
                  </motion.div>
                ) : null,
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* ── Desktop layout ── */}
        <div className="hidden h-full items-start pt-28 pb-10 lg:flex">
          <div className="mx-auto grid h-full w-full max-w-310 grid-cols-[220px_minmax(0,1fr)] gap-10 px-10">
            <div className="flex flex-col gap-3 pt-10">
              {EVENTS.map((event, index) => {
                const active = index === activeIndex;
                return (
                  <div key={event.titleHighlight} className="flex items-center gap-3">
                    <div className="relative h-12 w-0.5 overflow-hidden rounded-full bg-black/10">
                      <motion.div
                        className="absolute inset-x-0 bottom-0 rounded-full bg-brand"
                        initial={false}
                        animate={{ height: active ? "100%" : "24%" }}
                        transition={{ duration: reducedMotion ? 0 : 0.35, ease: [0.16, 1, 0.3, 1] }}
                      />
                    </div>
                    <div className="min-w-0">
                      <div className={`text-[12px] font-semibold tracking-[0.16em] ${active ? "text-black/55" : "text-black/28"}`}>
                        {String(index + 1).padStart(2, "0")}
                      </div>
                      <div className={`mt-1 text-[17px] font-semibold tracking-[-0.03em] ${active ? "text-ink" : "text-black/35"}`}>
                        {event.titleHighlight}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="relative h-full">
              <AnimatePresence mode="popLayout" initial={false}>
                {EVENTS.map((event, index) =>
                  index === activeIndex ? (
                    <motion.div
                      key={event.titleHighlight}
                      className="absolute inset-0"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                    >
                      <EventSpotlightCard
                        index={index + 1}
                        title={event.title}
                        titleHighlight={event.titleHighlight}
                        description={event.description}
                        imgSrc={event.imgSrc}
                      />
                    </motion.div>
                  ) : null,
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

/* ─── Member card ────────────────────────────────────────────────────── */
const MemberCard = memo(function MemberCard({ role, name, desc, img, style, open, onToggle }: {
  role: string; name: string; desc: string; img: string; style: React.CSSProperties;
  open: boolean; onToggle: () => void;
}) {
  const pointerDownX = useRef(0);

  return (
    <div
      onPointerDown={(e) => { pointerDownX.current = e.clientX; }}
      onClick={(e) => {
        if (Math.abs(e.clientX - pointerDownX.current) > 6) return;
        onToggle();
      }}
      className="relative shrink-0 overflow-hidden rounded-card cursor-pointer select-none w-46 sm:w-55 md:w-61 aspect-4/5"
      style={{ contain: "layout paint" }}
    >
      {/* Background photo */}
      <img
        alt={name}
        className="absolute max-w-none"
        src={img}
        decoding="async"
        loading="lazy"
        style={style}
      />

      {/* Bottom gradient + default label */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-36 bg-linear-to-t from-black/70 to-transparent" />
      <motion.div
        className="absolute bottom-0 left-0 right-0 p-3.5"
        animate={{ opacity: open ? 0 : 1, y: open ? -36 : 0 }}
        transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="text-[12px] font-semibold tracking-[0.14em] text-white/65 uppercase leading-none">{role}</div>
        <div className="mt-1.5 text-[24px] font-bold tracking-[-0.5px] text-white leading-[1.2]">{name}</div>
      </motion.div>

      {/* Blur layer — exits slowly so photo is revealed gradually */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="blur"
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            style={{ backdropFilter: "blur(16px)", background: "rgba(12,12,13,0.52)" }}
          />
        )}
      </AnimatePresence>

      {/* Content layer — exits faster so blur lingers after text is gone */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="content"
            className="absolute inset-0 flex flex-col justify-end p-4"
            initial={{ opacity: 0, y: 36 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 28 }}
            transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="text-[12px] font-semibold tracking-[0.14em] text-white/55 uppercase">{role}</div>
            <div className="mt-1.5 text-[28px] font-bold tracking-[-0.6px] text-white leading-[1.15]">{name}</div>
            <p className="mt-2.5 text-[15px] leading-[1.65] tracking-[-0.02em] text-white/78">{desc}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/* ─── Carousel hooks ─────────────────────────────────────────────────── */
type AnimRef = React.MutableRefObject<ReturnType<typeof animate> | null>;

function useCarouselLoop(
  trackRef: React.RefObject<HTMLDivElement | null>,
  x: MotionValue<number>,
  animRef: AnimRef,
) {
  const isHovering = useRef(false);
  const isCardOpen = useRef(false);

  // loopFnRef is updated every call so closures inside always read latest refs
  const loopFnRef = useRef<() => void>(() => {});
  loopFnRef.current = () => {
    if (isHovering.current || isCardOpen.current) return;
    const half = trackRef.current ? trackRef.current.scrollWidth / 2 : 0;
    if (!half) return;
    const mod = x.get() % half;
    const curr = mod > 0 ? mod - half : mod;
    x.set(curr);
    animRef.current = animate(x, -half, {
      duration: Math.abs(-half - curr) / 50,
      ease: "linear",
      onComplete: () => { x.set(0); loopFnRef.current(); },
    });
  };

  const pause = useCallback(() => {
    isHovering.current = true;
    animRef.current?.stop();
  }, [animRef]);

  const resume = useCallback(() => {
    isHovering.current = false;
    loopFnRef.current();
  }, []);

  const notifyCardOpen = useCallback((open: boolean) => {
    isCardOpen.current = open;
    animRef.current?.stop();
    if (!open && !isHovering.current) loopFnRef.current();
  }, [animRef]);

  useEffect(() => {
    const id = setTimeout(() => loopFnRef.current(), 200);
    return () => { clearTimeout(id); animRef.current?.stop(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { pause, resume, notifyCardOpen };
}

function useCarouselWheel(
  outerRef: React.RefObject<HTMLDivElement | null>,
  trackRef: React.RefObject<HTMLDivElement | null>,
  x: MotionValue<number>,
  animRef: AnimRef,
  onIdle: () => void,
) {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    let resumeTimer: ReturnType<typeof setTimeout> | null = null;
    const onWheel = (e: WheelEvent) => {
      const isHorizontalIntent =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) || (e.shiftKey && e.deltaY !== 0);
      if (!isHorizontalIntent) return;
      e.preventDefault();
      const half = trackRef.current ? trackRef.current.scrollWidth / 2 : 0;
      if (!half) return;
      const delta = Math.abs(e.deltaX) >= Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      const mod = (x.get() - delta) % half;
      x.set(mod > 0 ? mod - half : mod);
      animRef.current?.stop();
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => onIdleRef.current(), 1500);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      if (resumeTimer) clearTimeout(resumeTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/* ─── Members carousel ───────────────────────────────────────────────── */
const DOUBLED_MEMBERS = [...MEMBERS, ...MEMBERS] as const;
const DOUBLED_KEYS = DOUBLED_MEMBERS.map((m, i) => `${i}-${m.name}`);

function MembersCarousel() {
  const outerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const animRef = useRef<ReturnType<typeof animate> | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);

  const { pause, resume, notifyCardOpen } = useCarouselLoop(trackRef, x, animRef);
  useCarouselWheel(outerRef, trackRef, x, animRef, resume);

  useEffect(() => { notifyCardOpen(openKey !== null); }, [openKey, notifyCardOpen]);

  // Stable toggler per slot — ensures MemberCard memo is effective
  const togglers = useMemo(
    () => DOUBLED_KEYS.map((key) => () => setOpenKey(prev => prev === key ? null : key)),
    [],
  );

  return (
    <div
      ref={outerRef}
      className="w-full overflow-hidden"
      onMouseEnter={pause}
      onMouseLeave={() => { setOpenKey(null); resume(); }}
    >
      <motion.div
        ref={trackRef}
        style={{ x, width: "max-content" }}
        className="flex gap-3 sm:gap-4"
      >
        {DOUBLED_MEMBERS.map((member, i) => (
          <MemberCard
            key={DOUBLED_KEYS[i]}
            {...member}
            open={openKey === DOUBLED_KEYS[i]}
            onToggle={togglers[i]!}
          />
        ))}
      </motion.div>
    </div>
  );
}

/* ─── Main ───────────────────────────────────────────────────────────── */
export function Homepage({ onHeaderConfig, onHeroReady }: PageProps) {
  const [bgLoaded, setBgLoaded] = useState(false);
  const eventEndRef = useRef<HTMLDivElement>(null);
  const reducedMotion = !!useReducedMotion();

  const { activeSection, pastEventScroll } = useActiveSectionTracker(eventEndRef);

  const heroBgRef    = useRef<HTMLImageElement>(null);
  const marqueeBgRef = useRef<HTMLImageElement>(null);
  useParallaxEffect([
    { ref: marqueeBgRef, fn: (y) => `translateY(${(y - 2000) * -0.045}px)` },
  ]);
  useCursorFollower();
  useScrollProgress();
  useSectionBackground();

  useEffect(() => {
    const t = setTimeout(() => onHeroReady(), 80);
    return () => clearTimeout(t);
  }, [onHeroReady]);

  useEffect(() => {
    const headerSection = activeSection === "event" && pastEventScroll ? "event-post" : activeSection;
    onHeaderConfig({ activeSection: headerSection, darkHero: true });
  }, [activeSection, pastEventScroll, onHeaderConfig]);

  return (
    <>
      <style>{KEYFRAMES}</style>

      <div className="flex flex-col items-start w-full bg-snow">
        {/* ── Hero ──────────────────────────────────────────────── */}
        <div
          id="home"
          className="relative h-svh w-full shrink-0 overflow-hidden bg-ink"
        >
          {/* 배경 레이어 — overflow-hidden 없이 mask가 직접 이미지를 fade */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={bgLoaded ? { opacity: 1 } : undefined}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <img
              ref={heroBgRef}
              alt=""
              src={imgRectangle}
              className="absolute inset-0 w-full h-full object-cover object-center"
              onLoad={() => setBgLoaded(true)}
            />

            {glowColors.slice(0, 6).map((color, i) => (
              <div
                key={i}
                className="absolute rounded-full blur-[80px] opacity-40"
                style={{
                  backgroundColor: color,
                  width: "320px",
                  height: "320px",
                  left: `${15 + (i % 3) * 30}%`,
                  top: `${20 + Math.floor(i / 3) * 35}%`,
                  animation: `pulse-glow ${3 + (i % 3) * 0.6}s ease-in-out ${-(i * 0.5)}s infinite`,
                }}
              />
            ))}

            {/* 하단 fade — About 배경색(#0c0c0d)으로 자연스럽게 연결 */}
            <div
              className="absolute inset-x-0 bottom-0 pointer-events-none"
              style={{
                height: "50%",
                background:
                  "linear-gradient(to bottom, transparent 0%, rgba(12,12,13,0.6) 60%, #0c0c0d 100%)",
              }}
            />
          </motion.div>

          {/* Hero tagline */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 select-none">
            {["배우고 도전하며", "가치를 만드는 사람으로"].map(
              (line, i) => (
                <motion.div
                  key={i}
                  className="font-bold text-snow text-center leading-[1.2] pointer-events-none"
                  style={{
                    fontSize: "clamp(36px, 6vw, 88px)",
                    letterSpacing: "-0.03em",
                  }}
                  initial={
                    reducedMotion ? { opacity: 1 } : { opacity: 0, y: 36 }
                  }
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.9,
                    delay: 0.3 + i * 0.14,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  {line}
                </motion.div>
              ),
            )}
            <motion.button
              onClick={() => window.open("https://forms.gle/JSCZLUK7YQ6jvk5N6", "_blank")}
              className="mt-4 px-8 py-3.5 rounded-full font-semibold text-[18px] tracking-[-0.54px] leading-none cursor-pointer whitespace-nowrap"
              style={{
                color: "#fafafa",
                border: "1px solid rgba(255,255,255,0.4)",
                backdropFilter: "blur(8px)",
              }}
              initial={
                reducedMotion
                  ? { opacity: 1, background: "rgba(255,255,255,0.15)" }
                  : { opacity: 0, background: "rgba(255,255,255,0.15)" }
              }
              animate={{ opacity: 1, background: "rgba(255,255,255,0.15)" }}
              transition={{ duration: 0.35, delay: 0.9, ease: "easeOut" }}
              whileHover={
                reducedMotion
                  ? undefined
                  : {
                      y: -4,
                      scale: 1.04,
                      background: "rgba(255,255,255,0.34)",
                    }
              }
              whileTap={reducedMotion ? undefined : { scale: 0.98 }}
            >
              지원하러 가기 →
            </motion.button>
          </div>

          {/* Scroll hint */}
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
            style={{ animation: "scroll-bounce 1.8s ease-in-out 2s infinite" }}
          >
            <span className="font-medium text-snow text-[13px] tracking-[-0.42px] opacity-40">
              scroll
            </span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M10 4v12M4 10l6 6 6-6"
                stroke="#fafafa"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.4"
              />
            </svg>
          </div>
        </div>

        {/* ── About ─────────────────────────────────────────────── */}
        <section
          id="about"
          className="flex flex-col gap-25 items-center pt-40 pb-30 w-full bg-ink -mt-px"
        >
          <FadeUp
            threshold={0.12}
            className="flex flex-col items-center font-bold tracking-[-1.5px] text-center px-4"
          >
            <span
              className="leading-[1.3] text-snow"
              style={{ fontSize: SECTION_HEADING_SIZE }}
            >
              기획, 개발, 디자인을 함께 익히고
            </span>
            <span
              className="leading-[1.3] text-snow"
              style={{ fontSize: SECTION_HEADING_SIZE }}
            >
              배운 걸 실제 결과로 이어가요
            </span>
            <span
              className="mt-5 max-w-190 text-center font-medium leading-normal tracking-[-0.03em] text-white/62"
              style={{ fontSize: "clamp(16px,2vw,24px)" }}
            >
              스터디와 멘토링으로 배우고, 아이디어톤과 해커톤으로 직접
              부딪혀봐요
            </span>
          </FadeUp>

          <div className="mx-auto flex w-full max-w-290 flex-col items-center gap-10 px-4 md:px-10 lg:flex-row lg:justify-center lg:gap-12">
            {/* 텍스트 — 모바일에서 전면에 */}
            <SlideIn
              from="right"
              delay={150}
              className="flex min-w-0 w-full max-w-130 flex-col items-center justify-center gap-8 lg:items-start lg:text-left"
            >
              {/* 본문 — 모바일 핵심 콘텐츠 */}
            </SlideIn>
          </div>
        </section>

        {/* ── Stats ─────────────────────────────────────────────── */}
        <StatsSection />

        {/* ── History ───────────────────────────────────────────── */}
        <section
          id="history"
          className="flex flex-col items-center w-full"
          style={{
            background:
              "linear-gradient(to bottom, #0c0c0d 0%, #0e1628 30%, #00204d 55%)",
          }}
        >
          <div className="w-full px-6 pt-16 text-center md:px-16 lg:px-40">
            <div className="font-bold leading-[1.22] tracking-tight-xl text-snow" style={{ fontSize: SECTION_HEADING_SIZE }}>
              25년이
              <br />
              지금을 만들었어요
            </div>
          </div>
          <div className="w-full px-6 py-20 md:px-16 lg:px-40">
            <TimelineSection items={TIMELINE} />
          </div>
        </section>

        {/* ── History Bridge ────────────────────────────────────── */}
        <section
          id="history-bridge"
          className="relative isolate w-full overflow-hidden"
        >
          <div className="absolute inset-0">
            <img
              ref={marqueeBgRef}
              alt=""
              className="absolute inset-x-0 -top-[18%] h-[136%] w-full max-w-none object-cover object-center"
              src={imgDesktop26}
              style={{ willChange: "transform" }}
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(1,10,26,0.82)_0%,rgba(5,20,48,0.72)_30%,rgba(12,44,92,0.46)_58%,rgba(178,211,255,0.54)_78%,rgba(199,224,255,0.94)_100%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(255,255,255,0.16),transparent_36%),radial-gradient(circle_at_78%_28%,rgba(26,122,255,0.22),transparent_30%)]" />
            <div className="absolute inset-x-0 bottom-0 h-20 bg-[linear-gradient(180deg,rgba(199,224,255,0)_0%,rgba(199,224,255,0.82)_68%,#c7e0ff_100%)]" />
          </div>

          <div className="relative mx-auto flex min-h-100 w-full max-w-330 flex-col justify-start px-6 pt-24 pb-4 md:px-10 lg:min-h-155 lg:pt-32 lg:px-16">
            <div className="max-w-230">
              <h2 className="text-[clamp(34px,5.2vw,76px)] font-bold leading-[1.06] tracking-[-0.05em] text-white">
                우리도
                <br />
                그 흐름 위에 있어요
              </h2>
              <p className="mt-5 max-w-195 text-[clamp(18px,2.1vw,28px)] font-medium leading-[1.55] tracking-[-0.03em] text-white/76">
                선배들이 쌓아온 흐름을, 지금의 우리가 올해의 방식으로 이어가고 있어요.
              </p>
            </div>

          </div>
        </section>

        {/* ── Event ─────────────────────────────────────────────── */}
        <section id="event" className="flex flex-col items-center w-full">
          <div
            className="flex flex-col gap-16 items-center lg:px-10 pt-6 pb-6 md:py-20 w-full"
            style={{
              background:
                "linear-gradient(to bottom,#c7e0ff 0%,#c7e0ff 74%,#fafafa 100%)",
            }}
          >
            <div className="hidden w-full max-w-290 flex-col gap-6 px-4 md:px-8 lg:flex">
              <div className="max-w-230 font-bold leading-[1.22] tracking-tight-xl text-ink" style={{ fontSize: SECTION_HEADING_SIZE }}>
                한 해동안 함께할
                <br />
                주요 활동들이에요.
              </div>
            </div>

            <EventScrollShowcase />
            <div ref={eventEndRef} />

          </div>
        </section>

        {/* ── People ────────────────────────────────────────────── */}
        {/* <section id="people" className="flex flex-col items-center w-full">
          <SectionTitle text="PEOPLE" />

          <div className="flex flex-col gap-10 items-center pb-12 md:pb-20 w-full">
            <FadeUp
              threshold={0.2}
              className="flex flex-col items-center text-ink text-center px-10"
            >
              <span
                className="font-bold tracking-[-1.14px] leading-[1.3]"
                style={{ fontSize: "clamp(22px,3vw,38px)" }}
              >
                올해 게임웍스를 이끄는 팀이에요
              </span>
            </FadeUp>

            <MembersCarousel />

            <button
              className="border-b border-[#0c0c0d] flex items-center p-2 bg-transparent cursor-pointer group"
              onClick={navigateMembers}
            >
              <span className="font-medium text-ink text-[20px] leading-none group-hover:opacity-50 transition-opacity duration-200">
                이전 임원진도 보기 →
              </span>
            </button>
          </div>
        </section> */}

        <CTASection />

        {/* ── Footer ────────────────────────────────────────────── */}
        <Footer />
      </div>
    </>
  );
}
